import { Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import useRefreshToken from '@/hooks/useRefreshToken';
import useAuth from '@/hooks/useAuth';
import useLocalStorage from "@/hooks/useLocalStorage";
import { useGetCurrent } from '@/hooks/useGetCurrent';
import { useActivationGuard } from '@/hooks/useActivationGuard';
import { axiosPrivate } from '@/api/axios';
import { USER_API_PATH } from '@/lib/constants';

const PersistLogin = () => {
  const [isLoading, setIsLoading] = useState(true);
  const refresh = useRefreshToken();
  const auth = useAuth();
  const [persist] = useLocalStorage('persist', true);
  const getCurrent = useGetCurrent();
  const checkActivation = useActivationGuard();
  const navigate = useNavigate();

  if (!auth) {
    return <></>;
  }

  const { user, setCurrentUser } = auth;

  useEffect(() => {
    let isMounted = true;

    const verifyAndLoadUser = async () => {
      try {
        // 1. Refresh the token
        await refresh();

        // 2. Load full user profile
        const fullUser = await getCurrent();

        if (!isMounted) return;

        // 3. Patch lastLogin if needed
        const activation = checkActivation(fullUser);
        if (activation.shouldPatchLastLogin) {
          try {
            await axiosPrivate.patch(`/${USER_API_PATH}/${fullUser.id}`, {
              lastLogin: new Date().toISOString(),
            });
          } catch {
            // Non-critical
          }
        }

        // 4. Store in context
        setCurrentUser(fullUser);

        // 5. Handle activation redirects
        if (activation.shouldLogout) {
          auth.logOut();
          return;
        }
        if (activation.redirect) {
          navigate(activation.redirect, { replace: true });
        }
      } catch (err) {
        console.error('[PersistLogin]', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    !user?.accessToken && persist ? verifyAndLoadUser() : setIsLoading(false);

    return () => { isMounted = false; };
  }, []);

  return (
    <>
      {!persist
        ? <Outlet />
        : isLoading
          ? <p>Loading...</p>
          : <Outlet />
      }
    </>
  );
};

export default PersistLogin;
